import React from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ShiftDropDrawer from "./ShiftDropDrawer";
import { getCardColor2, get_t_str } from "../../../../../helpers/helpers";
import ShiftNoteInline from "./ShiftNoteInline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Lightbox } from "react-modal-image";

// HEIC/HEIF are accepted for upload but the backend re-encodes them to JPEG
// (browsers other than Safari can't render them); until that runs they show
// as a generic file rather than a broken image.
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

function EmployeeShiftCardUpdated(props) {
  function handleAddToPoolPress(note) {
    props.drop_shift(props.shift.id, null, note);
  }

  const shiftInfo = props.shift;

  let badgeText = "";
  if (shiftInfo?.badge === "is_pending_drop_by_user") {
    badgeText = "Drop Requested";
  } else if (
    shiftInfo?.badge === "is_trade_by_user_pending_employee_response"
  ) {
    badgeText = "Offered Trade";
  } else if (shiftInfo?.badge === "is_trade_by_user_pending_manager_response") {
    badgeText = "Offered Trade";
  } else if (shiftInfo?.badge === "is_trade_to_user_pending_self_response") {
    badgeText = "";
  } else if (shiftInfo?.badge === "is_trade_to_user_pending_manager_response") {
    badgeText = "";
  } else if (shiftInfo?.badge === "is_pending_pickup_by_user") {
    badgeText = "";
  } else {
    badgeText = "";
  }

  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const [lightboxImage, setLightboxImage] = React.useState(null);

  // The whole card opens the shift drawer, so attachment clicks must stop
  // propagation. Images open in a lightbox; other files open in a new tab.
  const openAttachment = (e, file) => {
    e.stopPropagation();
    if (!file.presigned_url) return;
    if (IMAGE_EXTENSIONS.includes((file.extension || "").toLowerCase())) {
      setLightboxImage(file.presigned_url);
    } else {
      window.open(file.presigned_url, "_blank", "noopener,noreferrer");
    }
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const shiftCardColor = getCardColor2(
    props.roleId?.id,
    props.roleNames,
    false
  );


  return (
    <>
      {lightboxImage && (
        <div className="absolute w-screen h-screen z-50">
          <Lightbox
            small={lightboxImage}
            medium={lightboxImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setLightboxImage(null)}
          />
        </div>
      )}
      <div
        className="w-full rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-300 px-4 py-3.5 cursor-pointer relative transition-colors"
        onClick={toggleDrawer(true)}
      >
        <div className="flex flex-row items-center relative">
          <div
            className={`w-1 rounded-full mr-3 self-stretch ${shiftCardColor}`}
            style={{ minHeight: "100%" }}
          />
          <div className="flex-1">
            <p className="text-xs text-slate-500">{props.dateFormatted}</p>
            <p className="text-slate-900 font-bold text-base mt-0.5">
              {props.shiftStartNumber}{props.shiftStartLetters?.toLowerCase() || ""} - {props.shiftEndNumber}{props.shiftEndLetters?.toLowerCase() || ""}
            </p>
            <p className="text-sm text-slate-500 mt-0.5">
              {props.station
                ? props.station
                : props.role
                ? props.role
                : props.roleType}{" "}
              &mdash; {props.location}
            </p>
          </div>
          <div className="text-slate-400">
            <ChevronRightIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
          </div>
          {/* Anchored to the header row (same spot as before) so it never covers the note */}
          {badgeText && (
            <div className="bg-amber-50 px-2 py-0.5 rounded-full absolute -bottom-1.5 -right-2 border border-amber-200">
              <p className="text-amber-700 text-xs">{badgeText}</p>
            </div>
          )}
        </div>
        <ShiftNoteInline note={props.note} maxLines={3} />
        {props.dailyEvents?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            {props.dailyEvents.map((event, index) => (
              <div key={index} className={index > 0 ? "mt-2" : ""}>
                <p className="text-xs font-semibold text-slate-900">{event.title}</p>
                {event.description && (
                  <p className="text-xs text-slate-500 mt-0.5">{event.description}</p>
                )}
                {event.start_time && (
                  <p className="text-xs text-violet-600 mt-0.5">
                    {get_t_str(event.start_time)}{event.end_time && ` - ${get_t_str(event.end_time)}`}
                  </p>
                )}
                {event.attachments?.length > 0 && (
                  <div className="mt-1.5 space-y-1">
                    {event.attachments.map((file) => {
                      const isImg = IMAGE_EXTENSIONS.includes((file.extension || "").toLowerCase());
                      return (
                        <button
                          key={file.uuid}
                          onClick={(e) => openAttachment(e, file)}
                          className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-violet-700 w-full text-left"
                        >
                          {isImg ? (
                            <img
                              src={file.presigned_url}
                              alt={file.displayName || file.name}
                              className="w-7 h-7 rounded object-cover shrink-0"
                            />
                          ) : (
                            <InsertDriveFileIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                          )}
                          <span className="truncate">{file.displayName || file.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <ShiftDropDrawer
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
        shiftInfo={shiftInfo}
        badgeText={badgeText}
        date={props.date}
        firstName={props.firstName}
        lastName={props.lastName}
        isDateInPast={props.isDateInPast}
        shiftStartNumber={props.shiftStartNumber}
        shiftStartLetters={props.shiftStartLetters}
        shiftEndNumber={props.shiftEndNumber}
        shiftEndLetters={props.shiftEndLetters}
        role={props.role}
        station={props.station}
        roleType={props.roleType}
        break={props.break}
        location={props.location}
        handleAddToPoolPress={handleAddToPoolPress}
        drop_shift={props.drop_shift}
        trade_shift={props.trade_shift}
        rescind_drop_shift={props.rescind_drop_shift}
        rescind_trade_shift={props.rescind_trade_shift}
        notMyUpcomingShifts={props.notMyUpcomingShifts}
        shiftCardColor={shiftCardColor}
        roleNames={props.roleNames}
        note={props.note}
        alsoWorking={props.alsoWorking}
      />
    </>
  );
}

export default EmployeeShiftCardUpdated;
